import { useState, useEffect, useRef } from 'react';
import { isUserSafari } from '../utils/safariUtils';
import { getWithExpiry, setWithExpiry } from '../utils/sessionStorage';
import { isUserFirefox } from '../utils/firefoxUtils';
import { S3UploadMetadata } from '../types/file';
import { GENERIC_ERROR_MESSAGES } from '../constants/permissionMessages';
import {
  logScreenerRecordingStarted,
  logScreenerRecordingCompleted,
  logScreenerCameraError,
  logScreenerUploadError,
} from '../utils/statsig';
import { getDeviceType } from '../utils/getDeviceType';
import { getBrowser } from '../utils/getBrowser';
import fixWebmDuration from 'fix-webm-duration';
import { TakeFailure } from '../types/interview';

// VI-431: how long to wait for the streaming socket to open before failing the attempt.
const WS_CONNECT_TIMEOUT_MS = 10000;

// VI-431: start failures the candidate can act on (mic busy or missing).
const isDeviceUnavailableError = (error: unknown) =>
  ['NotReadableError', 'NotFoundError', 'TrackStartError', 'DevicesNotFoundError'].includes(
    (error as { name?: string } | null)?.name ?? '',
  );

// VI-368: audio has no video frame to thumbnail, so each take uses a built-in
// placeholder tile in the Pick Your Answer strip.
const AUDIO_TAKE_PLACEHOLDER = '/images/audio-placeholder.jpg';

// VI-368: payload handed to the page after each take stops (multi-take group only).
type AudioTakeReadyPayload = {
  ws: WebSocket;
  blob: Blob;
  url: string;
  thumbnail: string;
  duration: number;
};

export function useAudio({ props }: { props?: { onboarding?: boolean } } = {}) {
  const onboarding = props?.onboarding || false;
  const isSafari = isUserSafari();
  const fallbackGuid = getWithExpiry('applicantGuid');
  const [isAudioActive, setIsAudioActive] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // for stream
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [transcription, setTranscription] = useState<string | null>(null);

  // ref for stream
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const currentQuestionIndexRef = useRef<number>(0);
  const wsRef = useRef<WebSocket | null>(null);
  // VI-368: duration of the last completed recording, captured in stop before
  // recorder.onstop fires (recordingStartTimeRef is cleared there). Mirrors useRecording.
  const lastRecordingDurationRef = useRef<number>(0);

  // VI-368: pre-warmed recording session — set up during the 3-2-1 countdown so
  // startPreparedAudioRecording() can call recorder.start() with zero latency.
  type PreparedAudioSession = {
    recorder: MediaRecorder;
    ws: WebSocket;
    stream: MediaStream;
    questionIndex: number;
    applicantGuid: string;
    onTakeReady?: (payload: AudioTakeReadyPayload) => void;
  };
  const preparedAudioSessionRef = useRef<PreparedAudioSession | null>(null);

  // audio devices
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  // Re-enabled - Track selected device from session storage to detect disconnections
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string>(
    getWithExpiry('selectedAudioDeviceId') || ''
  );
  const [audioDeviceRemoved, setAudioDeviceRemoved] = useState(false);
  // VI-431: set when the current recording attempt fails; cleared on the next attempt.
  const [takeFailure, setTakeFailure] = useState<TakeFailure | null>(null);
  // VI-431: true from recorder start until the take is delivered (or discarded)
  // by recorder.onstop — Submit must wait so it never pairs a stale take.
  const [isFinalizingTake, setIsFinalizingTake] = useState(false);
  // VI-431: bumped on every new or cancelled attempt so a superseded attempt's
  // late socket/timeout events can't fail (or hijack) the current one.
  const attemptIdRef = useRef(0);

  // audio level checking
  const [audioSilenceError, setAudioSilenceError] = useState<string | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const AUDIO_LEVEL_THRESHOLD = 1;
  const NO_AUDIO_THRESHOLD = 10; // seconds


  // errors
  const [error, setError] = useState<string | null>(null);

  const monitorAudioSilence = () => {
    try{
      setAudioSilenceError(null);
      if (!analyserRef.current) return;
    
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    
      const checkSilence = () => {
        if (!analyserRef.current) return;
    
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average);
    
        if (average >= AUDIO_LEVEL_THRESHOLD) {
          // Audio detected: clear silence timer if running
          if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
          }
          setAudioSilenceError(null);
        } else {
          // No audio detected: start/restart silence timer
          if (!silenceTimeoutRef.current) {
            silenceTimeoutRef.current = setTimeout(() => {
            setAudioSilenceError(`We can't hear you, please double check your audio devices. (${Date.now()})`);
            }, NO_AUDIO_THRESHOLD * 1000);
          }
        }
    
        animationFrameRef.current = requestAnimationFrame(checkSilence);
      };
    
      checkSilence();

    }catch(error){
      console.error('Failed to monitor audio silence:', error);
      setAudioSilenceError(`Failed to monitor audio silence. Please check your microphone permissions and try again. (${Date.now()})`);
    }
    
  };


  // COMMENTED OUT - No longer managing device selection from session storage
  // Browser will use default devices
  // useEffect(() => {
  //   // Only run if audioDevices has been populated
  //   if (audioDevices.length === 0) return;
  //   
  //   // Only keep real audioinput devices
  //   const realAudioDevices = audioDevices.filter(
  //     device =>
  //       device.kind === 'audioinput' &&
  //       device.deviceId !== 'default' &&
  //       device.deviceId !== 'communications'
  //   );
  // 
  //   if (realAudioDevices.length > 0) {
  //     const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
  //     if (!storedDeviceId) {
  //       const defaultDeviceId = realAudioDevices[0]?.deviceId;
  //       setSelectedAudioDeviceId(defaultDeviceId);
  //       setWithExpiry('selectedAudioDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000);
  //       console.log('Set default audio device:', defaultDeviceId, "device name: ", realAudioDevices[0]?.label);
  //     } else {
  //       const deviceExists = audioDevices.some(device => device.deviceId === storedDeviceId);
  //       if (!deviceExists && onboarding) {
  //         const newDefaultId = realAudioDevices[0]?.deviceId;
  //         setSelectedAudioDeviceId(newDefaultId);
  //         setWithExpiry('selectedAudioDeviceId', newDefaultId, 24 * 60 * 60 * 1000);
  //         console.log('Stored audio device not found, using new default:', newDefaultId);
  //       }
  //     }
  //   }
  // }, [audioDevices]);

  const getAudioDevices = async () => {
    try {
      setError(null);
      setAudioDeviceRemoved(false);
      try {
        console.warn("Getting audio devices...");
        await navigator.mediaDevices.getUserMedia({ audio: true });
  
        // Safari might need a small delay
        if (isUserSafari()) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error('Audio permission request failed:', error);
        setError("Could not access microphone. Please check your browser permissions and try again.");
        return [];
      }
  
      const devices = await navigator.mediaDevices.enumerateDevices();
      const micDevices = devices.filter(
        device =>
          device.kind === 'audioinput' &&
          device.deviceId !== 'default' &&
          device.deviceId !== 'communications'
      );
  
      // Only update if devices actually changed
      const devicesChanged = JSON.stringify(micDevices.map(d => d.deviceId)) !== 
                            JSON.stringify(audioDevices.map(d => d.deviceId));
  
      if (devicesChanged || audioDevices.length === 0) {
        console.log('Audio devices updated:', micDevices.length);
        setAudioDevices(micDevices);
      }
  
      return micDevices;
    } catch (err) {
      console.error('Failed to get audio devices:', err);
      setError("Could not access microphone. Please check your browser permissions and try again.");
      setAudioDeviceRemoved(true);
      return [];
    }
  };

  // COMMENTED OUT - No longer managing device selection from session storage
  // Browser will use default devices
  // useEffect(() => {
  //   if (audioDevices.length > 0) {
  //     // Filter out "default" and "communications"
  //     const realAudioDevices = audioDevices.filter(
  //       device => device.deviceId !== "default" && device.deviceId !== "communications"
  //     );
  // 
  //     // Get stored device ID if available
  //     const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
  // 
  //     if (!storedDeviceId) {
  //       // No stored selection, use first real device as default
  //       if (realAudioDevices.length > 0) {
  //         const defaultDeviceId = realAudioDevices[0]?.deviceId;
  //         setSelectedAudioDeviceId(defaultDeviceId);
  //         setWithExpiry('selectedAudioDeviceId', defaultDeviceId, 24 * 60 * 60 * 1000); // 24 hours
  //         console.log('Set default audio device:', defaultDeviceId);
  //       }
  //     } else {
  //       // Check if the stored device is still available (and not default/communications)
  //       const deviceExists = realAudioDevices.some(device => device.deviceId === storedDeviceId);
  // 
  //       if (!deviceExists && onboarding) {
  //         // Stored device not available, use first real device instead
  //         if (realAudioDevices.length > 0) {
  //           const newDefaultId = realAudioDevices[0].deviceId;
  //           setSelectedAudioDeviceId(newDefaultId);
  //           setWithExpiry('selectedAudioDeviceId', newDefaultId, 24 * 60 * 60 * 1000);
  //           console.log('Stored audio device not found, using new default:', newDefaultId, "device name: ", realAudioDevices[0]?.label);
  //         }
  //       } else {
  //         // Use the stored device
  //         setSelectedAudioDeviceId(storedDeviceId);
  //       }
  //     }
  //   }
  // }, [audioDevices]);

  // COMMENTED OUT - No longer polling for audio devices
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     getAudioDevices();
  //   }, 4000);
  // 
  //   return () => clearInterval(interval);
  // }, []);

  // COMMENTED OUT - No longer monitoring device changes or loading devices on mount
  // Device access will be requested when audio is actually started
  // useEffect(() => {
  //   // Initial load
  //   getAudioDevices();

  //   // Listen for device changes (plugging/unplugging devices)
  //   const handleDeviceChange = async () => {
  //     console.log('Audio device change detected, refreshing audio devices...');
  //     // await getAudioDevices();
  //   
  //     // Get the latest device list directly
  //     // After refreshing, check if selected device is still present
  //     const selectedAudioDeviceId = getWithExpiry('selectedAudioDeviceId');
  //     const realAudioDevices = audioDevices.filter(
  //       d => d.kind === "audioinput" &&
  //           d.deviceId !== "default" &&
  //           d.deviceId !== "communications"
  //     );
  //     const selectedAudioExists = realAudioDevices.some(
  //       d => d.deviceId === selectedAudioDeviceId
  //     );
  //   
  //     if (
  //       selectedAudioDeviceId &&
  //       selectedAudioDeviceId !== "default" &&
  //       selectedAudioDeviceId !== "communications" &&
  //       !selectedAudioExists
  //     ) {
  //       // Device was removed
  //       setRecordingError("Your microphone has been disconnected. Please reconnect your device and try again.");
  //       setError("Your microphone has been disconnected. Please reconnect your device and try again.");
  //       console.error("Audio device was removed");
  //       setAudioDeviceRemoved(true);
  //       setIsRecording(false);
  //     } else {
  //       // New device found or default state
  //       if(onboarding){
  //         console.warn("New audio device was found:", realAudioDevices[0]?.label, "\nUsing new deviceId: ", realAudioDevices[0]?.deviceId);
  //         setWithExpiry('selectedAudioDeviceId', realAudioDevices[0]?.deviceId, 24 * 60 * 60 * 1000);
  //         setRecordingError(null);
  //         setError(null);
  //         setAudioDeviceRemoved(false);
  //       }
  //     }
  //   };

  //   // Add event listener for device changes
  //   navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

  //   // Cleanup event listener on unmount
  //   return () => {
  //     navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  //   };
  // }, []);

  const checkingValidAudioDevice = async () => {
    console.log("Checking valid audio device");
    let micDenied = false;
    try{
      setRecordingError(null);
      setError(null);

      // If we're using a specific device ID, verify it via getUserMedia
      if (selectedAudioDeviceId) {
        try {
          const testStream = await navigator.mediaDevices.getUserMedia({ 
            audio: { deviceId: { exact: selectedAudioDeviceId } } 
          });
          testStream.getTracks().forEach(track => track.stop());
          console.log("Valid audio device (verified exact ID)");
          return true;
        } catch (exactError: any) {
          if (exactError?.name === 'OverconstrainedError') {
            // Stale device ID - refresh it with default device
            console.warn("Stored audio device ID is stale, refreshing with default...");
            const freshStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const freshTrack = freshStream.getAudioTracks()[0];
            if (freshTrack) {
              const freshId = freshTrack.getSettings().deviceId;
              if (freshId) {
                setSelectedAudioDeviceId(freshId);
                setWithExpiry('selectedAudioDeviceId', freshId, 24 * 60 * 60 * 1000);
              }
            }
            freshStream.getTracks().forEach(track => track.stop());
            console.log("Valid audio device (refreshed to default)");
            return true;
          }
          // NotAllowedError or any other error - let it propagate so the permission
          // check step handles it with the correct "blocked" messaging
          console.log("Audio device check error, deferring to permission check:", exactError?.name);
          throw exactError;
        }
      }
      
      // No specific device selected - verify default works
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      console.log("Valid audio device (default)");
      return true;
    }catch(error: any){
      console.error("Error checking valid audio device:", error);
      if (error && error.name === 'NotAllowedError') micDenied = true;
      if(micDenied){
        setRecordingError("Can't access your microphone. Please check your browser permissions and try again.");
      }else{
        setRecordingError("Your selected device for microphone is not available. Please try to reconnect your device and try again.");
      }
      return false;
    }
  }

  const startAudio = async (deviceId?: string) => {
    try {
      setError(null);
      setRecordingError(null);
      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Capture and store the actual microphone device ID being used
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const audioSettings = audioTrack.getSettings();
        if (audioSettings.deviceId) {
          console.log('🎤 startAudio: Storing actual microphone device ID:', audioSettings.deviceId);
          setSelectedAudioDeviceId(audioSettings.deviceId);
          setWithExpiry('selectedAudioDeviceId', audioSettings.deviceId, 24 * 60 * 60 * 1000);
        }
      }

      // Create audio context for visualization
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      // Start monitoring audio levels
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const checkAudioLevel = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(average);

        animationFrameRef.current = requestAnimationFrame(checkAudioLevel);
      };

      checkAudioLevel();
      setIsAudioActive(true);
      return true;
    } catch (error) {
      console.error('Error accessing microphone:', error);
      const constraints = {
        audio: deviceId ? { deviceId: { exact: deviceId } } : true
      };
      await handleMediaError(error, constraints);
      return false;
    }
  };

  

  const stopAudio = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    setAudioSilenceError(null);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    setIsAudioActive(false);
    setAudioLevel(0);
  };

  // VI-368: cancel a pre-warmed audio session that was never started (countdown
  // cancelled, or an error during prep).
  const cancelPreparedAudioSession = () => {
    // VI-431: invalidate any attempt still connecting (not yet in the session ref).
    // Never the one already recording (e.g. a Stop click landing in the same frame
    // the countdown handed over to the recorder) — its failures must still surface.
    if (mediaRecorderRef.current?.state !== 'recording') attemptIdRef.current++;
    const session = preparedAudioSessionRef.current;
    if (!session) return;
    if (session.ws.readyState !== WebSocket.CLOSED && session.ws.readyState !== WebSocket.CLOSING) {
      session.ws.close();
    }
    session.stream.getTracks().forEach(t => t.stop());
    preparedAudioSessionRef.current = null;
    console.log('[Pre-warm audio] Cancelled prepared session');
  };

  // VI-368: begin recording on a pre-warmed audio session (WS already open) so
  // recording starts with zero latency when the countdown reaches 0.
  const startPreparedAudioRecording = () => {
    const session = preparedAudioSessionRef.current;
    if (!session) {
      console.error('[Pre-warm audio] startPreparedAudioRecording: no prepared session');
      return;
    }
    mediaRecorderRef.current = session.recorder;
    audioStreamRef.current = session.stream;
    wsRef.current = session.ws;
    preparedAudioSessionRef.current = null;

    session.recorder.start(1000);
    setIsRecording(true);
    setIsFinalizingTake(true);
    recordingStartTimeRef.current = Date.now();
    currentQuestionIndexRef.current = session.questionIndex;
    monitorAudioSilence();
    logScreenerRecordingStarted({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: session.applicantGuid,
      deviceType: getDeviceType(),
      questionNumber: session.questionIndex + 1,
    });
    console.log('[Pre-warm audio] MediaRecorder started (from prepared session)');
  };

  // VI-368: set up stream + recorder + WebSocket during the 3-2-1 countdown so the
  // socket is open and the recorder is ready by the time the countdown hits 0.
  // Resolves true when the WS is open; false on any error (already surfaced).
  const prepareStreamedAudioRecording = async (
    applicantGuid: string = fallbackGuid,
    questionIndex: number,
    questionText: string,
    isLastQuestion: boolean = false,
    totalQuestions: number,
    s3MetaData: S3UploadMetadata,
    onTakeReady?: (payload: AudioTakeReadyPayload) => void
  ): Promise<boolean> => {
    cancelPreparedAudioSession();
    const attemptId = ++attemptIdRef.current;
    const isCurrentAttempt = () => attemptIdRef.current === attemptId;
    try {
      setRecordingError(null);
      setTakeFailure(null);
      setIsFinalizingTake(false);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      setIsRecording(false);
      setProgress(0);

      const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
      const constraints = {
        audio: storedDeviceId ? {
          deviceId: { exact: storedDeviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (!isCurrentAttempt()) {
        // Superseded while waiting for the mic (countdown cancelled).
        stream.getTracks().forEach(t => t.stop());
        return false;
      }

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack?.getSettings().deviceId) {
        setSelectedAudioDeviceId(audioTrack.getSettings().deviceId!);
        setWithExpiry('selectedAudioDeviceId', audioTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
      }

      const options = {
        mimeType: isSafari ? 'audio/mp4' : 'audio/webm',
        audioBitsPerSecond: 128000
      };
      // VI-431: per-attempt failure tracking — mirrors useRecording. Any failure
      // while this attempt is live (connecting, prepared during the countdown, or
      // recording) fails the take: the candidate is told, the take is never kept
      // or submittable, and it doesn't consume a try.
      let sessionWs: WebSocket | null = null;
      let wsSettled = false;
      let sessionFailed = false;
      let failureSurfaced = false;
      let recorderFinished = false;

      const recorder = new MediaRecorder(stream, options);
      const chunks: BlobPart[] = [];

      // Live = still connecting, prepared (countdown) or recording. After a normal
      // stop the take is complete locally, so a later socket drop is recovered on
      // Submit (HTTP fallback) and isn't a failure.
      const isSessionLive = () =>
        !wsSettled ||
        recorder.state === 'recording' ||
        (!!sessionWs && preparedAudioSessionRef.current?.ws === sessionWs);

      const surfaceFailure = (reason: TakeFailure['reason'], message: string) => {
        if (failureSurfaced || !isCurrentAttempt()) return;
        failureSurfaced = true;
        console.error(`[VI-431] Audio recording attempt failed (${reason}): ${message}`);
        setTakeFailure({ reason, message, at: Date.now() });
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        setAudioSilenceError(null);
        if (sessionWs && preparedAudioSessionRef.current?.ws === sessionWs) {
          // Failed during the countdown — drop the prepared session.
          preparedAudioSessionRef.current = null;
        }
        if (recorder.state === 'recording') {
          recorder.stop(); // onstop sees sessionFailed and discards the take
        }
        stream.getTracks().forEach(track => track.stop());
        if (sessionWs && (sessionWs.readyState === WebSocket.OPEN || sessionWs.readyState === WebSocket.CONNECTING)) {
          sessionWs.close();
        }
        setIsRecording(false);
      };

      const failTake = (reason: TakeFailure['reason'], message: string) => {
        if (sessionFailed || !isSessionLive()) return;
        sessionFailed = true;
        surfaceFailure(reason, message);
      };

      if (audioTrack) {
        audioTrack.onended = async () => {
          if (sessionFailed || !isSessionLive()) return;
          // Mark failed synchronously so a recorder auto-stop can't deliver this
          // partial take as a good one while we work out the message.
          sessionFailed = true;
          const storedMicId = getWithExpiry('selectedAudioDeviceId');
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = allDevices.filter(d => d.kind === 'audioinput' && d.deviceId !== 'default' && d.deviceId !== 'communications');
          const micStillExists = !storedMicId || audioInputs.some(d => d.deviceId === storedMicId);
          let errorMessage: string;
          if (!micStillExists) {
            errorMessage = 'Your microphone has been disconnected during recording. Please reconnect your microphone and try again.';
          } else {
            let micRevoked = false;
            try {
              const micPerm = await navigator.permissions.query({ name: 'microphone' as PermissionName });
              micRevoked = micPerm.state === 'denied';
            } catch { /* permissions API not supported */ }
            errorMessage = micRevoked
              ? 'Microphone access was revoked during recording. Please allow microphone access and try again.'
              : 'Microphone access was lost during recording. Please check your permissions and try again.';
          }
          // Actionable failure → specific guidance (AC5).
          surfaceFailure('device', errorMessage);
        };
      }

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(event.data);
          }
        }
      };

      recorder.onstop = async () => {
        recorderFinished = true;
        if (sessionFailed) {
          // VI-431: a failed attempt is never kept as a take or made submittable.
          setRecordedBlob(null);
          setRecordedAudioUrl(null);
          stream.getTracks().forEach(track => track.stop());
          setIsRecording(false);
          setIsFinalizingTake(false);
          return;
        }
        const mimeType = options.mimeType;
        const rawBlob = new Blob(chunks, { type: mimeType });

        // VI-702: MediaRecorder writes a streamable WebM with NO duration in the
        // header, so the native <audio> control can't show the total time or seek
        // (Firefox never resolves headerless Opus duration on its own). We already
        // know the length (wall-clock), so embed it into the WebM header here — a
        // fast header rewrite, NOT a re-encode, so it doesn't reintroduce the take-
        // delivery delay. Use whole seconds (×1000) so the file's total matches the
        // Pick Your Answer badge exactly. Only applies to WebM (Safari's mp4 already
        // carries duration); falls back to the raw blob if patching fails.
        let audioBlob = rawBlob;
        if ((mimeType || '').includes('webm')) {
          try {
            audioBlob = await fixWebmDuration(rawBlob, lastRecordingDurationRef.current * 1000);
          } catch {
            audioBlob = rawBlob;
          }
        }

        const url = URL.createObjectURL(audioBlob);
        setRecordedBlob(audioBlob);
        setRecordedAudioUrl(url);
        // Deliver the take with the same wall-clock duration that's baked into the
        // blob, so the badge and the native player's total stay consistent.
        if (onTakeReady) {
          onTakeReady({ ws, blob: audioBlob, url, thumbnail: AUDIO_TAKE_PLACEHOLDER, duration: lastRecordingDurationRef.current });
        }
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        setIsFinalizingTake(false);
      };

      recorder.onerror = () => {
        // The recorder may already be inactive when 'error' fires, so don't gate
        // on liveness — only on the attempt not having finished cleanly.
        if (sessionFailed || recorderFinished) return;
        sessionFailed = true;
        surfaceFailure('recorder', GENERIC_ERROR_MESSAGES.recording_failed.message);
      };

      const ws = new WebSocket(getWebSocketUrl());
      sessionWs = ws;

      const logSocketError = (errorCode: string, errorMessage: string) => {
        logScreenerUploadError({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid: getWithExpiry('applicantGuid') || undefined,
          deviceType: getDeviceType(),
          questionNumber: questionIndex + 1,
          errorCode,
          errorMessage,
        });
      };

      // Attached up front (not after stop) so a server error during the countdown
      // or recording is caught while the attempt is still live.
      ws.onmessage = (event) => {
        if (typeof event.data !== 'string') return;
        try {
          const parsedData = JSON.parse(event.data);
          if (parsedData.type === 'upload_complete') {
            setAudioUrl(parsedData.audioUrl);
            setTranscription(parsedData.transcription);
            setProgress(100);
          } else if (parsedData.type === 'error') {
            // Server-side processing failed (e.g. FFmpeg couldn't read the live
            // stream). The local recording is still complete, so don't fail the
            // take: on Submit the server rejects process_recording and the take is
            // re-uploaded over HTTP instead.
            console.error('Server error:', parsedData.message);
            logSocketError(parsedData.code || 'ServerUploadError', parsedData.message || 'Server returned error during audio upload');
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      return new Promise<boolean>((resolve) => {
        const settle = (v: boolean) => {
          if (wsSettled) return;
          wsSettled = true;
          clearTimeout(connectTimer);
          resolve(v);
        };

        // VI-431: never leave the candidate stuck on the countdown if the socket
        // neither opens nor errors.
        const connectTimer = setTimeout(() => {
          if (wsSettled) return;
          console.error('[Pre-warm audio] WebSocket connect timed out');
          logSocketError('WebSocketTimeout', 'WebSocket did not open in time during audio pre-warm');
          failTake('timeout', GENERIC_ERROR_MESSAGES.recording_failed.message);
          ws.close();
          stream.getTracks().forEach(track => track.stop());
          settle(false);
        }, WS_CONNECT_TIMEOUT_MS);

        ws.onopen = () => {
          // Failed while connecting, or superseded (countdown cancelled): release
          // everything instead of becoming the prepared session.
          if (sessionFailed || !isCurrentAttempt()) {
            ws.close();
            stream.getTracks().forEach(track => track.stop());
            return;
          }
          ws.send(JSON.stringify({
            type: 'metadata',
            applicantGuid,
            questionId: questionIndex,
            questionText,
            isLastQuestion,
            totalQuestions,
            s3MetaData,
          }));
          preparedAudioSessionRef.current = { recorder, ws, stream, questionIndex, applicantGuid, onTakeReady };
          settle(true);
        };

        ws.onerror = () => {
          logSocketError('WebSocketError', wsSettled ? 'WebSocket error during audio recording' : 'WebSocket connection error during audio pre-warm');
          failTake('connection', GENERIC_ERROR_MESSAGES.recording_failed.message);
          settle(false);
        };

        ws.onclose = () => {
          failTake('connection', GENERIC_ERROR_MESSAGES.recording_failed.message);
          if (!wsSettled) stream.getTracks().forEach(track => track.stop());
          settle(false);
        };
      });
    } catch (error: unknown) {
      // VI-431: a superseded attempt's start failure must not disturb the current one.
      if (!isCurrentAttempt()) return false;
      const isOverconstrained = error instanceof DOMException && error.name === 'OverconstrainedError';
      console.error('[Pre-warm audio] Setup failed:', error);
      if (isOverconstrained) {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const freshTrack = fallbackStream.getAudioTracks()[0];
          if (freshTrack?.getSettings().deviceId) {
            setSelectedAudioDeviceId(freshTrack.getSettings().deviceId!);
            setWithExpiry('selectedAudioDeviceId', freshTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
          }
          fallbackStream.getTracks().forEach(track => track.stop());
          return prepareStreamedAudioRecording(applicantGuid, questionIndex, questionText, isLastQuestion, totalQuestions, s3MetaData, onTakeReady);
        } catch {
          setError(GENERIC_ERROR_MESSAGES.device_constraint_error.message);
          setRecordingError(GENERIC_ERROR_MESSAGES.device_constraint_error.message);
          return false;
        }
      }
      const handled = await handleMediaError(error, { audio: true });
      if (!handled) {
        // VI-431: never leave a start failure silent — specific guidance when the
        // mic is busy or missing (AC5), generic otherwise.
        setTakeFailure({
          reason: 'start',
          message: isDeviceUnavailableError(error)
            ? GENERIC_ERROR_MESSAGES.microphone_unavailable_error.message
            : GENERIC_ERROR_MESSAGES.recording_failed.message,
          at: Date.now(),
        });
      }
      return false;
    }
  };

  const startStreamedAudioRecording = async (
    applicantGuid: string = fallbackGuid,
    questionIndex: number,
    questionText: string,
    isLastQuestion: boolean = false,
    totalQuestions: number,
    s3MetaData: S3UploadMetadata,
    onTakeReady?: (payload: AudioTakeReadyPayload) => void
  ) => {
    try {
      // Reset state
      setRecordingError(null);
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      setIsRecording(false);
      setProgress(0);
      
      // Use selected audio device if available, otherwise use browser default
      const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
      const constraints = {
        audio: storedDeviceId ? {
          deviceId: { exact: storedDeviceId },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } : {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      console.log('Using audio device for recording:', storedDeviceId || 'browser default');
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      // Capture and store the actual microphone device ID being used
      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        const audioSettings = audioTrack.getSettings();
        if (audioSettings.deviceId) {
          console.log('🎤 Audio Recording: Storing actual microphone device ID:', audioSettings.deviceId);
          setSelectedAudioDeviceId(audioSettings.deviceId);
          setWithExpiry('selectedAudioDeviceId', audioSettings.deviceId, 24 * 60 * 60 * 1000);
        }
      }
      
      // Create MediaRecorder with proper options 
      // safari supports mp4, but not webm
      const options = { 
        mimeType: isSafari ? 'audio/mp4' : 'audio/webm',
        audioBitsPerSecond: 128000 
      };
      console.log('Using MediaRecorder options:', options);
      const recorder = new MediaRecorder(stream, options);
      
      // Monitor for device disconnection or permission revocation during recording
      if (audioTrack) {
        audioTrack.onended = async () => {
          console.log('🎤 Audio track ended - checking if disconnection or permission revocation');
          
          // Check if the device is actually disconnected
          const storedMicId = getWithExpiry('selectedAudioDeviceId');
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          const audioDevices = allDevices.filter(d => 
            d.kind === 'audioinput' && 
            d.deviceId !== 'default' && 
            d.deviceId !== 'communications'
          );
          
          const micStillExists = !storedMicId || audioDevices.some(d => d.deviceId === storedMicId);
          
          let errorMessage: string;
          
          if (!micStillExists) {
            // Physical disconnection
            console.error('🎤 Microphone device physically disconnected during recording');
            errorMessage = 'Your microphone has been disconnected during recording. Please reconnect your microphone and try again.';
          } else {
            // Device still exists but track ended - likely permission revocation
            let micRevoked = false;
            try {
              const micPerm = await navigator.permissions.query({ name: 'microphone' as PermissionName });
              micRevoked = micPerm.state === 'denied';
            } catch { /* permissions API not supported */ }
            
            console.log('🎤 Permission check - Mic revoked:', micRevoked);
            
            if (micRevoked) {
              errorMessage = 'Microphone access was revoked during recording. Please allow microphone access and try again.';
            } else {
              errorMessage = 'Microphone access was lost during recording. Please check your permissions and try again.';
            }
          }
          
          setAudioDeviceRemoved(true);
          setRecordingError(`${errorMessage} (${Date.now()})`);
          
          // Stop the MediaRecorder if recording
          if (recorder.state === 'recording') {
            recorder.stop();
          }
          
          // Stop the stream
          stream.getTracks().forEach(track => track.stop());
          
          // Close WebSocket if open
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.close();
          }
          
          setIsRecording(false);
        };
      }
      
      // Set up WebSocket connection
      const socketUrl = getWebSocketUrl();
      console.log('Connecting to WebSocket:', socketUrl);
      const ws = new WebSocket(socketUrl);
      wsRef.current = ws;
      
      // Track socket state and local recording
      let socketConnected = false;
      let recordingStarted = false;
      const chunks: BlobPart[] = [];
      
      // Handle WebSocket connection open
      ws.onopen = async () => {
        console.log('WebSocket connection established');
        socketConnected = true;

        // Send metadata before starting recording
      ws.send(JSON.stringify({
        type: 'metadata',
        applicantGuid,
        questionId: questionIndex,
        questionText,
        isLastQuestion,
        totalQuestions,
        s3MetaData
      }));

      setIsRecording(true);
      await monitorAudioSilence();
      if (!recordingStarted) {
        recorder.start(1000); // Send data every 1 second
        recordingStarted = true;
        recordingStartTimeRef.current = Date.now();
        currentQuestionIndexRef.current = questionIndex;
        logScreenerRecordingStarted({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid,
          deviceType: getDeviceType(),
          questionNumber: questionIndex + 1,
        });
        console.log('MediaRecorder started');
      }
      };
      
      // Handle WebSocket errors
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setRecordingError(`Connection error. Please try again. (${Date.now()})`);

        logScreenerUploadError({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid: getWithExpiry('applicantGuid') || undefined,
          deviceType: getDeviceType(),
          questionNumber: questionIndex + 1,
          errorCode: 'WebSocketError',
          errorMessage: 'WebSocket connection error during audio streaming',
        });

        if (recorder.state === 'recording') recorder.stop();
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Handle WebSocket closure
      ws.onclose = () => {
        console.log('WebSocket connection closed');
        socketConnected = false;
      };

      // Stream data chunks as they become available
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          // Store chunk locally for replay
          chunks.push(event.data);
          
          // Send chunk to server if socket is connected
          if (socketConnected) {
            console.log(`Sending audio chunk: ${Math.round(event.data.size / 1024)} KB`);
            ws.send(event.data);
          }
        }
      };
      
      // Handle recording stop
      recorder.onstop = () => {
        console.log('MediaRecorder stopped');
        
        // Create local audio blob from collected chunks
        const mimeType = options.mimeType;
        const audioBlob = new Blob(chunks, { type: mimeType });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Save the local recording reference
        setRecordedBlob(audioBlob);
        setRecordedAudioUrl(audioUrl);

        // VI-368: hand the completed take to the page (multi-take group). The
        // take's WebSocket is passed so the page can finalize the chosen take and
        // close the rest; an audio placeholder stands in for the video thumbnail.
        if (onTakeReady) {
          onTakeReady({
            ws,
            blob: audioBlob,
            url: audioUrl,
            thumbnail: AUDIO_TAKE_PLACEHOLDER,
            duration: lastRecordingDurationRef.current,
          });
        }

        // Handle WebSocket messages for processing results
        ws.onmessage = (event) => {
          console.log('WebSocket message data:', event.data);
          
          try {
            const parsedData = JSON.parse(event.data);
            
            if (parsedData.type === 'upload_complete') {
              // For audio, we only need audioUrl and transcription
              setAudioUrl(parsedData.audioUrl);
              setTranscription(parsedData.transcription);
              setProgress(100);
            } else if (parsedData.type === 'error') {
              console.error('Server error:', parsedData.message);
              setRecordingError(`Something went wrong. Please try again. (${Date.now()})`);

              logScreenerUploadError({
                jobId: getWithExpiry('jobId') || '',
                companyId: getWithExpiry('companyId') || '',
                applicantGuid: getWithExpiry('applicantGuid') || undefined,
                deviceType: getDeviceType(),
                questionNumber: questionIndex + 1,
                errorCode: parsedData.code || 'ServerUploadError',
                errorMessage: parsedData.message || 'Server returned error during audio upload',
              });
            }else if (parsedData.type === 'metadata_received') {
              console.log('Server received metadata');
            } else if (parsedData.type === 'answer_submitted') {
              console.log('Answer submitted to API', parsedData.isReconciled);
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        // Clean up media stream
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };
      
      // Store recorder and stream for later access
      mediaRecorderRef.current = recorder;
      audioStreamRef.current = stream;
      
      // If socket connected before recorder was ready
      if (socketConnected && !recordingStarted) {
        recorder.start(1000);
        recordingStarted = true;
        setIsRecording(true);
        recordingStartTimeRef.current = Date.now();
        currentQuestionIndexRef.current = questionIndex;
        logScreenerRecordingStarted({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid,
          deviceType: getDeviceType(),
          questionNumber: questionIndex + 1,
        });
        await monitorAudioSilence();
      }
      
    } catch (error: unknown) {
      const isOverconstrained = error instanceof DOMException && error.name === 'OverconstrainedError';
      const failedConstraint = isOverconstrained && 'constraint' in error ? (error as any).constraint : 'unknown';
      console.error('Failed to start streaming audio recording:', error);
      console.error('Constraint that failed:', failedConstraint);
      setIsRecording(false);

      if (isOverconstrained) {
        console.log('Retrying audio getUserMedia with relaxed constraints...');
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('Fallback audio getUserMedia succeeded, proceeding with default device');

          const freshTrack = fallbackStream.getAudioTracks()[0];
          if (freshTrack?.getSettings().deviceId) {
            setSelectedAudioDeviceId(freshTrack.getSettings().deviceId!);
            setWithExpiry('selectedAudioDeviceId', freshTrack.getSettings().deviceId!, 24 * 60 * 60 * 1000);
          }

          fallbackStream.getTracks().forEach(track => track.stop());
          return startStreamedAudioRecording(applicantGuid, questionIndex, questionText, isLastQuestion, totalQuestions, s3MetaData);
        } catch (fallbackError) {
          console.error('Fallback audio getUserMedia also failed:', fallbackError);
          setError(GENERIC_ERROR_MESSAGES.device_constraint_error.message);
          setRecordingError(GENERIC_ERROR_MESSAGES.device_constraint_error.message);
          return;
        }
      }

      await handleMediaError(error, { audio: true });
      if (!error) {
        setError('Recording failed. Please check your microphone and try again.');
        setRecordingError('Recording failed. Please check your microphone and try again.');
      }
    }
  };

  const stopStreamedAudioRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('Stopping streamed audio recording');

      if (recordingStartTimeRef.current) {
        const recordingLengthSeconds = Math.round((Date.now() - recordingStartTimeRef.current) / 1000);
        lastRecordingDurationRef.current = recordingLengthSeconds; // VI-368: read by recorder.onstop
        logScreenerRecordingCompleted({
          jobId: getWithExpiry('jobId') || '',
          companyId: getWithExpiry('companyId') || '',
          applicantGuid: getWithExpiry('applicantGuid') || undefined,
          deviceType: getDeviceType(),
          questionNumber: currentQuestionIndexRef.current + 1,
          recordingLengthSeconds,
        });
        recordingStartTimeRef.current = null;
      }

      mediaRecorderRef.current.stop();

      setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log("User has stopped recording");
        }
      }, 500); // Small delay to ensure all chunks are sent
    }
  };

  // Returns true when a specific, actionable message was set (VI-431: callers
  // fall back to the generic "Recording failed" message otherwise).
  const handleMediaError = async (error: any, constraints: MediaStreamConstraints): Promise<boolean> => {
    console.log("handleMediaError called")
    console.error("handleMediaError: ", error)

    logScreenerCameraError({
      jobId: getWithExpiry('jobId') || '',
      companyId: getWithExpiry('companyId') || '',
      applicantGuid: getWithExpiry('applicantGuid') || undefined,
      deviceType: getDeviceType(),
      browser: getBrowser(),
      errorCode: error?.name,
      errorMessage: error?.message || String(error),
    });

      // browser permission denied
      let micDenied = false;

      //overconstrained
      let micConstrained = false;

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e: any) {
        if (e && e.name === 'NotAllowedError') micDenied = true;
        if(e && e.name === 'OverconstrainedError') micConstrained = true;
      }

      if (micDenied) {
        setError("Can't access your microphone. Please check your browser permissions and try again.");
        setRecordingError("Can't access your microphone. Please check your browser permissions and try again.");
        console.error("Can't access your microphone. Please check your browser permissions and try again.");
      } else if (micConstrained) {
        setError("Your selected device for microphone is not available. Please try to reconnect your device and try again.");
        setRecordingError("Your selected device for microphone is not available. Please try to reconnect your device and try again.");
        console.error("Your selected device for microphone is not available. Please try to reconnect your device and try again. (micConstrained)");
      }
      console.log("micDenied", micDenied)
      console.log("micConstrained", micConstrained)
      setIsRecording(false);
      return micDenied || micConstrained;
    };

  // this will be used to submit the recording to the server
  const handleSubmitStreamRecording = () => {
    try{
      setError(null);
      setRecordingError(null);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'process_recording',
        }));
        console.log('Sent process_recording signal to server');
      }
      else {
        console.error('Cannot submit recording: WebSocket is not connected');
      }
    }catch(error){
      console.error('Failed to submit streaming audio recording:', error);
      setRecordingError(`Failed to submit recording. Please check your microphone and try again. (${Date.now()})`);
      throw error;
    }
    
  };

  const getWebSocketUrl = () => {
    const isDev =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    const isHttps = window.location.protocol === 'https:';

    // HTTPS dev: route through the Vite dev server's /stream-audio proxy
    // (ws:true in vite.config.ts) so TLS terminates at :3000 and is forwarded as
    // plain ws:// to the backend on :4001. Direct wss://localhost:4001 fails
    // because the backend WS server doesn't speak TLS in dev. Mirrors useRecording.
    if (isDev && isHttps) {
      return `wss://${window.location.host}/stream-audio`;
    }

    if (isDev) {
      return `ws://${window.location.hostname}:4001/stream-audio`;
    }

    // Production: same host, no port manipulation (proxied at infra level).
    return `${isHttps ? 'wss:' : 'ws:'}//${window.location.host}/stream-audio`;
  };

  const toggleAudio = async (deviceId?: string) => {
    try{
      setError(null);
      if (isAudioActive) {
        stopAudio();
        return false;
      } else {
        console.log('Starting audio with deviceId:', deviceId || selectedAudioDeviceId);
        return await startAudio(deviceId || selectedAudioDeviceId);
      }
    } catch (error) { 
      console.error('Failed to toggle audio:', error);
      setError("Could not access microphone. Please check your browser permissions and try again.");
      return null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  // Check if the selected microphone device is still available
  // Uses getUserMedia as the real verification (not just enumerateDevices which can have stale IDs)
  const checkSelectedMicDeviceAvailable = async (): Promise<{ available: boolean; deviceId: string | null }> => {
    try {
      const storedDeviceId = getWithExpiry('selectedAudioDeviceId');
      
      // If no device was previously selected, return true (will use default)
      if (!storedDeviceId) {
        console.log('🎤 No specific microphone device selected, will use browser default');
        return { available: true, deviceId: null };
      }

      // Verify by actually trying to access the device with getUserMedia
      // This catches stale device IDs that enumerateDevices might miss
      try {
        const testStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: storedDeviceId } }
        });
        // Device is genuinely available
        testStream.getTracks().forEach(track => track.stop());
        console.log('🎤 Selected microphone device verified via getUserMedia:', storedDeviceId);
        return { available: true, deviceId: storedDeviceId };
      } catch (getUserMediaError: any) {
        if (getUserMediaError?.name === 'OverconstrainedError') {
          // Device ID doesn't exist anymore - likely stale after permission toggle
          // Clear the stale ID and update with a fresh one from default device
          console.warn('🎤 Stored mic device ID is stale (OverconstrainedError), refreshing...');
          try {
            const freshStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const freshTrack = freshStream.getAudioTracks()[0];
            if (freshTrack) {
              const freshId = freshTrack.getSettings().deviceId;
              if (freshId) {
                console.log('🎤 Updated mic device ID to:', freshId);
                setSelectedAudioDeviceId(freshId);
                setWithExpiry('selectedAudioDeviceId', freshId, 24 * 60 * 60 * 1000);
              }
            }
            freshStream.getTracks().forEach(track => track.stop());
            return { available: true, deviceId: null };
          } catch {
            // Refresh also failed - defer to permission check which will show the correct message
            console.log('🎤 Mic refresh failed, deferring to permission check');
            return { available: true, deviceId: storedDeviceId };
          }
        } else {
          // NotAllowedError or any other error - defer to permission check step
          // This ensures consistent "blocked" messaging instead of "device not available"
          console.log('🎤 Mic device check error, deferring to permission check:', getUserMediaError?.name);
          return { available: true, deviceId: storedDeviceId };
        }
      }
    } catch (err) {
      console.error('Error checking microphone device availability:', err);
      return { available: false, deviceId: null };
    }
  };

  return {
    isAudioActive,
    audioLevel,
    startAudio,
    stopAudio,
    toggleAudio,
    isRecording,
    setIsRecording,
    recordingError,
    progress,
    recordedBlob,
    recordedAudioUrl,
    audioUrl,
    transcription,
    resetAudioAndTranscription: () => {
      setAudioUrl(null);
      setTranscription(null);
    },
    startStreamedAudioRecording,
    prepareStreamedAudioRecording,
    startPreparedAudioRecording,
    cancelPreparedAudioSession,
    stopStreamedAudioRecording,
    audioDevices,
    selectedAudioDeviceId,
    setSelectedAudioDeviceId,
    getAudioDevices,
    handleSubmitStreamRecording,
    error,
    audioSilenceError,
    audioDeviceRemoved,
    checkingValidAudioDevice,
    setRecordingError,
    setError,
    checkSelectedMicDeviceAvailable,
    audioStream: streamRef.current,
    // VI-431
    takeFailure,
    clearTakeFailure: () => setTakeFailure(null),
    getActiveSocket: () => wsRef.current,
    isFinalizingTake,
  };
}
